import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";
import TvOutlinedIcon from "@mui/icons-material/TvOutlined";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import { tokens } from "../theme";
import { ItemFormSurface } from "./EditDrawer";
import type { SignageScreenGroup } from "@shared/types/signage";
import { PermissionGate } from "../auth/access";

/**
 * The rows the picker lists: UNITS (UNIT-3), the spots a group holds. A
 * screen swapped in a unit is in the group the moment it is in the unit.
 */
export interface GroupPickerUnit {
  /** `rooms.id`. */
  id: string;
  /** What somebody calls it - the unit's number. */
  label: string;
  /** What hangs there now, so two "Reception"s differ. */
  code: string;
  /** Which location it is in. Blank for a scope that has only one. */
  premiseLabel: string;
  /** How it is hung. A group of both shapes only takes "fits both" content. */
  orientation?: "landscape" | "portrait";
}

/** Longer than any name anybody needs, and short enough to fit a table cell. */
const NAME_LIMIT = 100;

const ALL_LOCATIONS = "__all__";

/**
 * Make or edit a screen group.
 *
 * ## Why the chosen screens sit ABOVE the list
 *
 * The list is long and filtered; the selection is the thing being built. Once
 * the list scrolls, a selection rendered only as ticks inside it is invisible -
 * you cannot answer "what have I picked" without scrolling back through
 * everything. So the selection is its own summary, with a chip per screen that
 * can be removed from there, and the ticks in the list stay as the second view
 * of the same fact.
 *
 * ## Why it shows which groups a screen is already in
 *
 * Membership is many-to-many on purpose, so "is this screen already covered"
 * is a real question with a non-obvious answer, and it is the one an operator
 * asks while building the third group over the same wall.
 */
export default function SignageGroupDialog({
  open,
  editing,
  screens,
  groups,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  /** The group being edited, or null to make a new one. */
  editing: SignageScreenGroup | null;
  screens: GroupPickerUnit[];
  /** Every group in scope, for the "already in" column. */
  groups: SignageScreenGroup[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  /** Persist. Resolves true on success, which closes the surface. */
  onSave: (input: { name: string; unitIds: string[] }) => Promise<boolean>;
}) {
  const { t } = useTranslation(["signage", "common", "glossary"]);
  const [name, setName] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [premise, setPremise] = useState(ALL_LOCATIONS);

  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? "");
    setChosen(editing?.unitIds ?? []);
    setQuery("");
    setPremise(ALL_LOCATIONS);
  }, [open, editing]);

  const premises = useMemo(
    () =>
      [...new Set(screens.map((s) => s.premiseLabel).filter(Boolean))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [screens]
  );

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return screens.filter(
      (s) =>
        (premise === ALL_LOCATIONS || s.premiseLabel === premise) &&
        (!q ||
          s.label.toLowerCase().includes(q) ||
          s.code.toLowerCase().includes(q))
    );
  }, [screens, query, premise]);

  /** screenId -> the groups it is already in. Built once, not per row. */
  const memberships = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const g of groups) {
      if (editing && g.id === editing.id) continue;
      for (const id of g.unitIds) {
        const list = map.get(id);
        if (list) list.push(g.name);
        else map.set(id, [g.name]);
      }
    }
    return map;
  }, [groups, editing]);

  const byId = useMemo(() => new Map(screens.map((s) => [s.id, s])), [screens]);
  /** Landscape and portrait units both chosen: only "fits both" content. */
  const mixed = useMemo(
    () =>
      new Set(
        chosen.flatMap((id) => {
          const o = byId.get(id)?.orientation;
          return o ? [o] : [];
        })
      ).size > 1,
    [chosen, byId]
  );

  const toggle = (id: string) =>
    setChosen((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  return (
    <ItemFormSurface
      mode={editing ? "edit" : "create"}
      open={open}
      onClose={onClose}
      title={
        editing
          ? t("signageGroupDialog.editTitle")
          : t("signageGroupDialog.newTitle")
      }
      subtitle={t("signageGroupDialog.subtitle")}
      icon={<CollectionsOutlinedIcon />}
      maxWidth="md"
      drawerWidth={820}
      actions={(dismiss) => (
        <>
          <Button onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Box sx={{ flex: 1 }} />
          <PermissionGate
            resource="signage"
            level="edit"
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              disabled={saving || !name.trim()}
              onClick={() =>
                // The page saves; on success the surface slides away first, and
                // only then does the page close it (via onClose).
                void onSave({ name: name.trim(), unitIds: chosen }).then(
                  (ok) => ok && dismiss()
                )
              }
            >
              {saving
                ? t("common:states.saving")
                : t("signageGroupDialog.saveGroup")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Typography
        variant="bodySm"
        sx={{ fontWeight: "fontWeightSemibold", mb: 0.75 }}
      >
        {t("signageGroupDialog.groupName")}
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={t("signageGroupDialog.namePlaceholder")}
        value={name}
        onChange={(e) => setName(e.target.value.slice(0, NAME_LIMIT))}
        inputProps={{
          "aria-label": t("signageGroupDialog.groupName"),
          maxLength: NAME_LIMIT,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                {name.length} / {NAME_LIMIT}
              </Typography>
            </InputAdornment>
          ),
        }}
      />

      {/* -- what has been chosen so far -------------------------------- */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 3, mb: 1 }}
      >
        <Typography sx={{ fontWeight: "fontWeightBold" }}>
          {t("signageGroupDialog.selectedUnits", { count: chosen.length })}
        </Typography>
        {chosen.length > 0 && (
          <Button size="small" onClick={() => setChosen([])}>
            {t("signageGroupDialog.clearSelection")}
          </Button>
        )}
      </Stack>

      {chosen.length === 0 ? (
        <Stack
          direction="row"
          alignItems="center"
          gap={2}
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "10px",
            p: 2.5,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: tokens.brandTintSoft,
              color: tokens.brand,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
            }}
          >
            <TvOutlinedIcon />
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: "fontWeightBold" }}>
              {t("signageGroupDialog.noUnitsSelected")}
            </Typography>
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              {t("signageGroupDialog.pickUnitsHint")}
            </Typography>
          </Box>
        </Stack>
      ) : (
        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "10px",
            p: 1.5,
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          {chosen.map((id) => (
            <Chip
              key={id}
              size="small"
              icon={<TvOutlinedIcon />}
              /* A chosen screen the list no longer offers is still a member.
                   Naming it by id rather than dropping it silently is the only
                   way an operator can see that something moved. */
              label={byId.get(id)?.label ?? id}
              onDelete={() => toggle(id)}
              sx={{ bgcolor: tokens.brandTintSoft }}
            />
          ))}
        </Box>
      )}

      {/* -- the list to choose from ------------------------------------ */}
      <Typography sx={{ fontWeight: "fontWeightBold", mt: 3, mb: 1.5 }}>
        {t("signageGroupDialog.chooseUnits")}
      </Typography>
      {mixed && (
        <Typography
          variant="bodySm"
          data-testid="group-mixed-note"
          sx={{ color: tokens.warning, mb: 1 }}
        >
          {t("signageGroupDialog.mixedNote")}
        </Typography>
      )}
      <Stack direction="row" gap={1.5} sx={{ mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t("signageGroupDialog.searchUnitsPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          inputProps={{ "aria-label": t("signageGroupDialog.searchUnits") }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        {premises.length > 1 && (
          <TextField
            select
            size="small"
            value={premise}
            onChange={(e) => setPremise(e.target.value)}
            inputProps={{ "aria-label": t("signageGroupDialog.location") }}
            sx={{ minWidth: 190 }}
          >
            <MenuItem value={ALL_LOCATIONS}>
              {t("common:labels.allLocations")}
            </MenuItem>
            {premises.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>

      <Box
        sx={{
          border: `1px solid ${tokens.divider}`,
          borderRadius: "10px",
          // Capped in the create modal only; in the edit drawer the drawer
          // body is what scrolls, and a second scroller inside it fights it.
          ...(editing ? {} : { maxHeight: 320, overflowY: "auto" }),
        }}
      >
        {shown.length === 0 ? (
          <Typography
            variant="bodySm"
            sx={{ p: 3, textAlign: "center", color: tokens.inkMuted }}
          >
            {t("signageGroupDialog.noUnitMatches")}
          </Typography>
        ) : (
          shown.map((s, i) => {
            const inGroups = memberships.get(s.id) ?? [];
            return (
              <Stack
                key={s.id}
                direction="row"
                alignItems="center"
                gap={1.5}
                onClick={() => toggle(s.id)}
                sx={{
                  px: 1.5,
                  py: 1,
                  cursor: "pointer",
                  borderTop: i === 0 ? "none" : `1px solid ${tokens.divider}`,
                  "&:hover": { bgcolor: tokens.rowAlt },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "8px",
                    bgcolor: tokens.brandTintSoft,
                    color: tokens.brand,
                    display: "grid",
                    placeItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <TvOutlinedIcon fontSize="small" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: "fontWeightSemibold" }}
                  >
                    {s.label}
                  </Typography>
                  <Typography
                    variant="meta"
                    noWrap
                    sx={{ color: tokens.inkMuted }}
                  >
                    {s.code}
                    {s.orientation
                      ? ` · ${
                          s.orientation === "portrait"
                            ? t("common:labels.portrait")
                            : t("common:labels.landscape")
                        }`
                      : ""}
                    {s.premiseLabel ? ` · ${s.premiseLabel}` : ""}
                  </Typography>
                </Box>
                {/* Membership is many-to-many, so "already covered" is a real
                      question - and the one asked while building a third group
                      over the same wall. */}
                <Stack direction="row" gap={0.5} sx={{ flexShrink: 0 }}>
                  {inGroups.slice(0, 2).map((g) => (
                    <Chip
                      key={g}
                      label={g}
                      size="small"
                      sx={{ bgcolor: tokens.rowAlt, typography: "micro" }}
                    />
                  ))}
                  {inGroups.length > 2 && (
                    <Chip
                      label={`+${inGroups.length - 2}`}
                      size="small"
                      sx={{ bgcolor: tokens.rowAlt, typography: "micro" }}
                    />
                  )}
                </Stack>
                <Checkbox
                  checked={chosen.includes(s.id)}
                  // The row is the control; the box would otherwise toggle
                  // twice on a click that lands on it.
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(s.id)}
                  inputProps={{
                    "aria-label": t("signageGroupDialog.selectUnit", {
                      name: s.label,
                    }),
                  }}
                />
              </Stack>
            );
          })
        )}
      </Box>

      {error && (
        <Typography variant="bodySm" sx={{ mt: 2, color: tokens.error }}>
          {error}
        </Typography>
      )}
    </ItemFormSurface>
  );
}
